import React from 'react';
import styled from 'styled-components';
import { FaLinkedin, FaGithub, FaArrowDown } from 'react-icons/fa';
import HeroImg from '../assets/images/Marlo_Profile_Pic.png';
import Button from './Button';

const HeroStyles = styled.div`
  .hero {
    min-height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 12rem 0 8rem;
    overflow: hidden;
  }
  
  /* Animated background elements */
  .hero::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 20s ease-in-out infinite;
  }
  
  .hero::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(118, 75, 162, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 15s ease-in-out infinite reverse;
  }
  
  @keyframes float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(30px, -30px) scale(1.1); }
  }
  
  .hero__content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;
    position: relative;
    z-index: 1;
  }
  
  .hero__text {
    animation: slideInLeft 0.8s ease-out;
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .hero__greeting {
    font-size: 1.8rem;
    font-weight: 500;
    color: var(--accent-primary);
    margin-bottom: 1rem;
    display: inline-block;
    animation: fadeIn 0.8s ease-out 0.2s both;
  }
  
  .hero__name {
    font-size: 6.5rem;
    font-weight: 800;
    margin-bottom: 2rem;
    line-height: 1.1;
    background: linear-gradient(135deg, #ffffff 0%, #b8b8d4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: fadeIn 0.8s ease-out 0.4s both;
  }
  
  .hero__title {
    font-size: 3rem;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 2rem;
    animation: fadeIn 0.8s ease-out 0.6s both;
    
    .gradient-text {
      background: var(--gradient-primary);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .hero__description {
    font-size: 1.7rem;
    line-height: 2;
    color: var(--text-secondary);
    margin-bottom: 3rem;
    max-width: 550px;
    font-weight: 300;
    animation: fadeIn 0.8s ease-out 0.8s both;
  }
  
  .hero__cta {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
    animation: fadeIn 0.8s ease-out 1s both;
  }
  
  .hero__image-wrapper {
    position: relative;
    animation: slideInRight 0.8s ease-out;
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .hero__image-container {
    position: relative;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
  
  .hero__image-bg {
    position: absolute;
    top: -20px;
    right: -20px;
    width: 100%;
    height: 100%;
    background: var(--gradient-primary);
    border-radius: var(--radius-xl);
    opacity: 0.3;
    animation: pulse 3s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50% { transform: scale(1.05); opacity: 0.5; }
  }
  
  .hero__image {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    border-radius: var(--radius-xl);
    overflow: hidden;
    border: 2px solid var(--glass-border);
    box-shadow: var(--shadow-lg);
    background: var(--glass-bg);
    backdrop-filter: blur(10px);
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: var(--transition-slow);
    }
    
    &:hover img {
      transform: scale(1.05);
    }
  }
  
  .hero__quote {
    position: absolute;
    bottom: -30px;
    left: -30px;
    background: var(--card-bg);
    backdrop-filter: blur(10px);
    padding: 2rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--glass-border);
    max-width: 300px;
    box-shadow: var(--shadow-lg);
    animation: fadeIn 0.8s ease-out 1.2s both;
    
    p {
      font-size: 1.4rem;
      font-style: italic;
      color: var(--text-secondary);
      line-height: 1.6;
      margin-bottom: 0.5rem;
    }
    
    .quote-author {
      font-size: 1.2rem;
      color: var(--accent-primary);
      font-weight: 600;
      font-style: normal;
    }
  }
  
  .hero__social {
    position: fixed;
    left: 4rem;
    bottom: 4rem;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    z-index: 10;
    
    a {
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--glass-bg);
      border: 1px solid var(--glass-border);
      border-radius: var(--radius-sm);
      color: var(--text-primary);
      font-size: 2.2rem;
      transition: var(--transition-normal);
      backdrop-filter: blur(10px);
      
      &:hover {
        background: var(--gradient-primary);
        transform: translateY(-5px);
        box-shadow: var(--shadow-glow);
        border-color: transparent;
      }
    }
  }
  
  .hero__scroll {
    position: absolute;
    bottom: 4rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    
    span {
      font-size: 1.3rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 2px;
    }
    
    .scroll-icon {
      font-size: 2.4rem;
      color: var(--accent-primary);
      animation: bounce 2s ease-in-out infinite;
    }
    
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(10px); }
    }
  }

  @media only screen and (max-width: 968px) {
    .hero__content {
      grid-template-columns: 1fr;
      gap: 4rem;
      text-align: center;
    }
    
    .hero__text {
      order: 2;
    }
    
    .hero__image-wrapper {
      order: 1;
    }
    
    .hero__name {
      font-size: 5rem;
    }
    
    .hero__title {
      font-size: 2.4rem;
    }
    
    .hero__description {
      margin: 0 auto 3rem;
    }
    
    .hero__cta {
      justify-content: center;
    }
    
    .hero__quote {
      position: static;
      margin: 2rem auto 0;
    }
    
    .hero__social {
      left: 2rem;
      bottom: 2rem;
      
      a {
        width: 45px;
        height: 45px;
        font-size: 2rem;
      }
    }
  }
  
  @media only screen and (max-width: 768px) {
    .hero {
      padding: 10rem 0 6rem;
    }
    
    .hero__name {
      font-size: 4rem;
    }
    
    .hero__title {
      font-size: 2rem;
    }
    
    .hero__description {
      font-size: 1.6rem;
    }
    
    .hero__image-container {
      max-width: 350px;
    }
    
    .hero__social {
      position: static;
      flex-direction: row;
      justify-content: center;
      margin-top: 3rem;
    }
    
    .hero__scroll {
      display: none;
    }
  }
`;

export default function HeroSection() {
  return (
    <HeroStyles>
      <div className="hero">
        <div className="container">
          <div className="hero__content">
            <div className="hero__text">
              <span className="hero__greeting">👋 Hello, I'm</span>
              <h1 className="hero__name">Marlo Rouse</h1>
              <h2 className="hero__title">
                Full-Stack <span className="gradient-text">Developer</span> & <span className="gradient-text">Designer</span>
              </h2>
              <p className="hero__description">
                I craft beautiful, functional web experiences with modern technologies. 
                Passionate about turning ideas into elegant solutions that make an impact.
              </p>
              <div className="hero__cta">
                <Button btnLink="/projects" btnText="View My Work" outline={false} />
                <Button btnLink="/contact" btnText="Get In Touch" outline={true} />
              </div>
            </div>
            
            <div className="hero__image-wrapper">
              <div className="hero__image-container">
                <div className="hero__image-bg"></div>
                <div className="hero__image">
                  <img src={HeroImg} alt="Marlo Rouse" />
                </div>
                <div className="hero__quote">
                  <p>"Today's accomplishments were yesterday's impossibilities"</p>
                  <span className="quote-author">— Robert H. Schuller</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="hero__social">
          <a 
            href="http://www.linkedin.com/in/marlo-rouse-80125b23b" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin />
          </a>
          <a 
            href="https://github.com/Panthers217" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="GitHub"
          >
            <FaGithub />
          </a>
        </div>
        
        <div className="hero__scroll">
          <span>Scroll</span>
          <FaArrowDown className="scroll-icon" />
        </div>
      </div>
    </HeroStyles>
  );
}
